import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clientsApi, ddnsApi } from '../services/api';
import { DdnsServeur, DdnsServeurInput } from '../types';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { formatDateCourte } from '../utils/desktopVersions';
import {
  ajouterUnAn,
  blocIniPoste,
  blocIniServeur,
  connexion,
  dateAlger,
  formatHorodatage,
  formatVuIlYa,
  nomServeurValide,
  pastilleExpiration,
  proposerNomServeur,
} from '../utils/ddns';
import DdnsPostes from './DdnsPostes';
import './Pages.css';
import './Ddns.css';

// Page Accès distant : abonnements « DDNS maison ». Le service de fichiers installé sur le serveur
// du client signale son adresse toutes les 2 minutes ; les postes distants du même client la
// demandent avant de se connecter. Le serveur refait tous les contrôles ; le jeton du serveur n'est
// montré qu'une fois, à la création et à la régénération.

interface ClientOption {
  id: number;
  client_name: string;
}

interface Formulaire {
  id_client: string;
  nom_serveur: string;
  date_debut: string;
  date_expiration: string;
  notes: string;
}

interface ModalState {
  mode: 'create' | 'edit';
  id?: number;
  clientInitial?: string;
  nomModifie: boolean;
  initial: Formulaire;
  form: Formulaire;
}

interface JetonAffiche {
  // 'postes' : un jeton d'ENRÔLEMENT des postes distants du client, qui les fait entrer (puis,
  // pendant la tolérance, leur sert directement à demander l'adresse). Les deux autres montrent le
  // jeton du SERVICE, qui autorise à écrire cette adresse — à ne pas coller sur les postes.
  contexte: 'creation' | 'regeneration' | 'postes';
  client: string;
  nom: string;
  jeton: string;
  copie: boolean;
  directJusquAu?: string | null;
}

type Filtre = 'all' | 'hors_ligne' | 'bientot' | 'expire' | 'suspendu' | 'attente';

const NOTES_MAX = 4000;
const RAFRAICHISSEMENT_MS = 60000;

const messageErreur = (err: any, defaut: string): string =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || defaut;

// 404 ou 409 : la liste affichée ne correspond plus à la base (modifiée depuis un autre poste).
const listePerimee = (err: any): boolean => [404, 409].includes(err?.response?.status);

const normaliserNotes = (notes: string): string => notes.replace(/\r\n?/g, '\n').trim();

const memeSaisie = (a: Formulaire, b: Formulaire): boolean =>
  a.id_client === b.id_client && a.nom_serveur === b.nom_serveur && a.date_debut === b.date_debut &&
  a.date_expiration === b.date_expiration && normaliserNotes(a.notes) === normaliserNotes(b.notes);

const LIBELLES_CONNEXION = { en_ligne: 'En ligne', hors_ligne: 'Hors ligne', jamais_vu: 'Jamais vu' };

const BlocIni: React.FC<{ titre: string; texte: string; onCopier: (texte: string) => void; id: string }> = ({
  titre,
  texte,
  onCopier,
  id,
}) => (
  <div className="ad-bloc-ini">
    <div className="ad-bloc-ini-entete">
      <span id={id}>{titre}</span>
      <button type="button" className="btn btn-secondary" onClick={() => onCopier(texte)} aria-describedby={id}>
        Copier
      </button>
    </div>
    <pre aria-labelledby={id}>{texte}</pre>
  </div>
);

const Ddns: React.FC = () => {
  const [items, setItems] = useState<DdnsServeur[]>([]);
  const [loading, setLoading] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<Filtre>('all');
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [rechercheClient, setRechercheClient] = useState('');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [jeton, setJeton] = useState<JetonAffiche | null>(null);
  const [postesDe, setPostesDe] = useState<DdnsServeur | null>(null);
  const [maintenant, setMaintenant] = useState(() => new Date());
  const { hasPermission } = useAuth();
  const { showToast, showConfirm } = useToast();

  const canCreate = hasPermission('ddns', 'create');
  const canEdit = hasPermission('ddns', 'edit');
  const canRenew = hasPermission('ddns', 'renew');
  const canDelete = hasPermission('ddns', 'delete');

  const loadData = useCallback(async (silencieux = false) => {
    try {
      if (!silencieux) {
        setLoading(true);
      }
      const response = await ddnsApi.getAll();
      const data = response.data?.items;
      setItems(Array.isArray(data) ? data : []);
      setMaintenant(new Date());
    } catch (err: any) {
      if (!silencieux) {
        showToast(messageErreur(err, 'Chargement des abonnements impossible'), 'error');
      }
    } finally {
      if (!silencieux) {
        setLoading(false);
      }
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // « En ligne » est calculé par le serveur : la liste se rafraîchit seule chaque minute.
  useEffect(() => {
    const minuteur = window.setInterval(() => loadData(true), RAFRAICHISSEMENT_MS);
    return () => window.clearInterval(minuteur);
  }, [loadData]);

  const loadClients = useCallback(async () => {
    if (clients.length > 0) {
      return;
    }
    try {
      const response = await clientsApi.getAll();
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.clients || [];
      setClients(
        data
          .map((client: any) => ({ id: Number(client.id), client_name: String(client.client_name ?? '') }))
          .sort((a: ClientOption, b: ClientOption) => a.client_name.localeCompare(b.client_name, 'fr'))
      );
    } catch (err: any) {
      showToast(messageErreur(err, 'Chargement des clients impossible'), 'error');
    }
  }, [clients.length, showToast]);

  const stats = useMemo(() => {
    const compte = { en_ligne: 0, hors_ligne: 0, jamais_vu: 0, bientot: 0, expire: 0, suspendu: 0, attente: 0 };
    items.forEach((item) => {
      compte[connexion(item)] += 1;
      compte.attente += item.postes_en_attente || 0;
      const code = pastilleExpiration(item).code;
      if (code === 'bientot' || code === 'grace') {
        compte.bientot += 1;
      } else if (code === 'expire' || code === 'suspendu') {
        compte[code] += 1;
      }
    });
    return compte;
  }, [items]);

  const filteredItems = useMemo(() => {
    const texte = recherche.trim().toLowerCase();
    return items.filter((item) => {
      if (texte && !item.client_name.toLowerCase().includes(texte) && !item.nom_serveur.includes(texte) &&
          !(item.ip || '').includes(texte)) {
        return false;
      }
      const code = pastilleExpiration(item).code;
      switch (filtre) {
        case 'hors_ligne':
          return connexion(item) !== 'en_ligne';
        case 'bientot':
          return code === 'bientot' || code === 'grace';
        case 'expire':
        case 'suspendu':
          return code === filtre;
        case 'attente':
          return (item.postes_en_attente || 0) > 0;
        default:
          return true;
      }
    });
  }, [items, recherche, filtre]);

  // ---- Formulaire : mêmes règles que le serveur ------------------------------------------
  const formCheck = useMemo(() => {
    if (!modal) {
      return { valid: false, client: '', nom: '', dates: '', notes: '', inchange: false };
    }
    const { form } = modal;
    let nom = '';
    if (form.nom_serveur === '') {
      nom = 'Nom obligatoire';
    } else if (!nomServeurValide(form.nom_serveur)) {
      nom = '3 à 40 caractères : minuscules sans accent, chiffres et tirets, sans tiret au début ni à la fin';
    } else {
      const autre = items.find((item) => item.nom_serveur === form.nom_serveur && item.id !== modal.id);
      if (autre) {
        nom = `Ce nom est déjà utilisé (${autre.client_name}).`;
      }
    }
    const client = form.id_client === '' ? 'Choisir le client' : '';
    let dates = '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_debut) || !/^\d{4}-\d{2}-\d{2}$/.test(form.date_expiration)) {
      dates = 'Dates de début et d’expiration obligatoires';
    } else if (form.date_expiration < form.date_debut) {
      dates = 'L’expiration précède le début';
    }
    const notes = form.notes.length > NOTES_MAX ? `${NOTES_MAX} caractères au plus` : '';
    const inchange = modal.mode === 'edit' && memeSaisie(modal.initial, form);
    return { valid: !nom && !client && !dates && !notes && !inchange, client, nom, dates, notes, inchange };
  }, [modal, items]);

  const updateForm = (patch: Partial<Formulaire>) =>
    setModal((courant) => (courant ? { ...courant, form: { ...courant.form, ...patch } } : courant));

  const openCreate = () => {
    const debut = dateAlger();
    const form: Formulaire = {
      id_client: '',
      nom_serveur: '',
      date_debut: debut,
      date_expiration: ajouterUnAn(debut),
      notes: '',
    };
    setRechercheClient('');
    setModal({ mode: 'create', nomModifie: false, initial: form, form });
    loadClients();
  };

  const openEdit = (item: DdnsServeur) => {
    const form: Formulaire = {
      id_client: String(item.id_client),
      nom_serveur: item.nom_serveur,
      date_debut: item.date_debut,
      date_expiration: item.date_expiration,
      notes: item.notes || '',
    };
    setRechercheClient('');
    setModal({ mode: 'edit', id: item.id, clientInitial: item.client_name, nomModifie: true, initial: form, form });
    loadClients();
  };

  // Création : tant que le nom n'a pas été retouché, il suit le client choisi.
  const choisirClient = (valeur: string) =>
    setModal((courant) => {
      if (!courant) {
        return courant;
      }
      const form = { ...courant.form, id_client: valeur };
      if (courant.mode === 'create' && !courant.nomModifie) {
        const client = clients.find((option) => String(option.id) === valeur);
        form.nom_serveur = client
          ? proposerNomServeur(client.client_name, client.id, items.map((item) => item.nom_serveur))
          : '';
      }
      return { ...courant, form };
    });

  const changerDebut = (valeur: string) =>
    setModal((courant) => {
      if (!courant) {
        return courant;
      }
      const form = { ...courant.form, date_debut: valeur };
      // En création, l'expiration par défaut (début + 1 an) suit le début
      if (courant.mode === 'create' && courant.form.date_expiration === ajouterUnAn(courant.form.date_debut)) {
        form.date_expiration = ajouterUnAn(valeur);
      }
      return { ...courant, form };
    });

  const optionsClients = useMemo(() => {
    const texte = rechercheClient.trim().toLowerCase();
    const liste = texte ? clients.filter((client) => client.client_name.toLowerCase().includes(texte)) : clients;
    if (modal?.form.id_client && !liste.some((client) => String(client.id) === modal.form.id_client)) {
      const choisi = clients.find((client) => String(client.id) === modal.form.id_client);
      return [choisi || { id: Number(modal.form.id_client), client_name: modal.clientInitial || `Client ${modal.form.id_client}` },
        ...liste];
    }
    return liste;
  }, [clients, rechercheClient, modal]);

  const enregistrer = async (etat: ModalState) => {
    const { form } = etat;
    const payload: DdnsServeurInput = {
      id_client: Number(form.id_client),
      nom_serveur: form.nom_serveur,
      date_debut: form.date_debut,
      date_expiration: form.date_expiration,
      notes: form.notes.trim() === '' ? null : form.notes,
    };
    try {
      setSaving(true);
      if (etat.mode === 'create') {
        const response = await ddnsApi.create(payload);
        const item: DdnsServeur | undefined = response.data?.item;
        setModal(null);
        setJeton({
          contexte: 'creation',
          client: item?.client_name || '',
          nom: item?.nom_serveur || payload.nom_serveur,
          jeton: response.data?.jeton || '',
          copie: false,
        });
        showToast(`Abonnement ${payload.nom_serveur} créé`, 'success');
      } else {
        await ddnsApi.update(etat.id as number, payload);
        setModal(null);
        showToast(`Abonnement ${payload.nom_serveur} modifié`, 'success');
      }
      await loadData();
    } catch (err: any) {
      showToast(messageErreur(err, 'Enregistrement impossible'), 'error');
      if (listePerimee(err)) {
        await loadData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!modal || !formCheck.valid) {
      return;
    }
    const etat = modal;
    if (etat.mode === 'edit') {
      const consequences: string[] = [];
      if (etat.initial.nom_serveur !== etat.form.nom_serveur) {
        consequences.push(
          `Le nom passe de ${etat.initial.nom_serveur} à ${etat.form.nom_serveur} : il faut remplacer Server_Name ` +
          'dans WinalcoFichiers.ini (puis redémarrer le service) et sur chaque poste distant. Le jeton ne change pas.'
        );
      }
      if (etat.initial.id_client !== etat.form.id_client) {
        consequences.push(
          'Le client change : seuls les postes du nouveau client recevront l’adresse.'
        );
      }
      if (consequences.length > 0) {
        showConfirm(consequences.join(' '), () => { void enregistrer(etat); }, 'Modifier l’abonnement');
        return;
      }
    }
    void enregistrer(etat);
  };

  // ---- Actions sur une ligne ------------------------------------------------------------
  const runAction = async (item: DdnsServeur, action: () => Promise<any>, succes: (data: any) => string) => {
    try {
      setBusyId(item.id);
      const response = await action();
      showToast(succes(response?.data), 'success');
      await loadData();
      return response?.data;
    } catch (err: any) {
      showToast(messageErreur(err, 'Action impossible'), 'error');
      if (listePerimee(err)) {
        await loadData();
      }
      return null;
    } finally {
      setBusyId(null);
    }
  };

  const libelle = (item: DdnsServeur) => `${item.client_name} (${item.nom_serveur})`;

  const handleRenew = (item: DdnsServeur) => {
    const expire = item.etat === 'EXPIRE';
    showConfirm(
      `Renouveler l’accès distant de ${libelle(item)} ? Expiration : ${formatDateCourte(item.date_expiration)} ` +
        `→ ${formatDateCourte(item.expiration_si_renouvelee)}.` +
        (expire ? ' L’abonnement est expiré : les postes retrouvent l’adresse dès le renouvellement.' : '') +
        (item.etat === 'SUSPENDU' ? ' Il reste suspendu : le réactiver ensuite.' : ''),
      () => runAction(item, () => ddnsApi.renew(item.id),
        (data) => `${item.nom_serveur} renouvelé jusqu’au ${formatDateCourte(data?.nouvelle_expiration)}`),
      'Renouveler l’abonnement'
    );
  };

  const handleSuspend = (item: DdnsServeur) => {
    showConfirm(
      `Suspendre l’accès distant de ${libelle(item)} ? Les postes ne recevront plus l’adresse ; ` +
        'le serveur du client continue de la signaler. Réactivable à tout moment.',
      () => runAction(item, () => ddnsApi.suspend(item.id), () => `${item.nom_serveur} suspendu`),
      'Suspendre l’abonnement'
    );
  };

  const handleResume = (item: DdnsServeur) => {
    showConfirm(
      `Réactiver l’accès distant de ${libelle(item)} ?` +
        (item.jours_restants >= 0
          ? ' Les postes du client recevront de nouveau l’adresse.'
          : ' L’abonnement est expiré : il faudra aussi le renouveler.'),
      () => runAction(item, () => ddnsApi.resume(item.id), () => `${item.nom_serveur} réactivé`),
      'Réactiver l’abonnement'
    );
  };

  const handleRegenerate = (item: DdnsServeur) => {
    showConfirm(
      `Générer un nouveau jeton pour ${libelle(item)} ? L’ancien cesse de fonctionner immédiatement : ` +
        'le service du client sera refusé tant que le nouveau jeton n’est pas collé dans WinalcoFichiers.ini ' +
        '(puis le service redémarré).',
      async () => {
        const data = await runAction(item, () => ddnsApi.regenerateToken(item.id), () => 'Nouveau jeton généré');
        if (data?.jeton) {
          setJeton({ contexte: 'regeneration', client: item.client_name, nom: item.nom_serveur, jeton: data.jeton, copie: false });
        }
      },
      'Nouveau jeton'
    );
  };

  // Jetons et postes : volet à part (DdnsPostes). Un jeton neuf s'affiche une fois, dans la même
  // fenêtre que le jeton du service, avec le bloc à coller sur les postes.
  const montrerJetonPostes = (item: DdnsServeur, valeur: string, directJusquAu: string | null) =>
    setJeton({
      contexte: 'postes',
      client: item.client_name,
      nom: item.nom_serveur,
      jeton: valeur,
      copie: false,
      directJusquAu,
    });

  const handleDelete = (item: DdnsServeur) => {
    showConfirm(
      `Supprimer définitivement l’abonnement ${libelle(item)} ? Les postes ne pourront plus obtenir ` +
        'l’adresse et le jeton ne vaudra plus rien.',
      () => runAction(item, () => ddnsApi.remove(item.id), () => `Abonnement ${item.nom_serveur} supprimé`),
      'Supprimer l’abonnement'
    );
  };

  // ---- Jeton : copie et fermeture --------------------------------------------------------
  // Presse-papiers moderne d'abord ; s'il refuse (fenêtre sans focus, droit refusé), copie par
  // sélection. Si les deux échouent, le texte reste sélectionnable dans la fenêtre.
  const copierParSelection = (texte: string): boolean => {
    const zone = document.createElement('textarea');
    zone.value = texte;
    zone.setAttribute('readonly', '');
    zone.style.position = 'fixed';
    zone.style.opacity = '0';
    document.body.appendChild(zone);
    zone.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(zone);
    }
  };

  const copier = async (texte: string, jetonCompris: boolean) => {
    let copie = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(texte);
        copie = true;
      } catch {
        copie = false;
      }
    }
    if (!copie) {
      copie = copierParSelection(texte);
    }
    if (!copie) {
      showToast('Copie impossible : sélectionnez le texte et copiez-le à la main', 'error');
      return;
    }
    if (jetonCompris) {
      setJeton((courant) => (courant ? { ...courant, copie: true } : courant));
    }
    showToast('Copié dans le presse-papiers', 'success');
  };

  const fermerJeton = () => {
    if (jeton && !jeton.copie) {
      showConfirm(
        'Le bloc du serveur n’a pas été copié avec le bouton « Copier ». Une fois cette fenêtre fermée, le jeton ' +
          'ne pourra plus être affiché : il faudra en générer un nouveau. Fermer quand même ?',
        () => setJeton(null),
        'Fermer sans copier ?'
      );
      return;
    }
    setJeton(null);
  };

  return (
    <div className="page-container acces-distant">
      <div className="page-header">
        <div>
          <h1>Accès distant</h1>
          <p className="ad-subtitle">
            Abonnements DDNS : l&apos;adresse du serveur de chaque client, tenue à jour par son service de fichiers.
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => loadData()} disabled={loading}>
            Actualiser
          </button>
          {canCreate && (
            <button className="btn btn-primary" onClick={openCreate}>
              Nouvel abonnement
            </button>
          )}
        </div>
      </div>

      <div className="ad-stats" role="group" aria-label="Filtres rapides">
        <button type="button" className={`ad-stat ${filtre === 'all' ? 'ad-stat-actif' : ''}`} onClick={() => setFiltre('all')}
          aria-pressed={filtre === 'all'}>
          <span className="ad-stat-nombre">{items.length}</span> abonnement{items.length > 1 ? 's' : ''}
        </button>
        <span className="ad-stat ad-stat-info" title="Dernier signal il y a moins de 10 minutes">
          <span className="ad-point ad-point-en_ligne" aria-hidden="true" />
          <span className="ad-stat-nombre">{stats.en_ligne}</span> en ligne
        </span>
        <button type="button" className={`ad-stat ${filtre === 'hors_ligne' ? 'ad-stat-actif' : ''}`}
          onClick={() => setFiltre('hors_ligne')} aria-pressed={filtre === 'hors_ligne'}>
          <span className="ad-point ad-point-hors_ligne" aria-hidden="true" />
          <span className="ad-stat-nombre">{stats.hors_ligne + stats.jamais_vu}</span> hors ligne ou jamais vus
        </button>
        <button type="button" className={`ad-stat ${filtre === 'bientot' ? 'ad-stat-actif' : ''}`}
          onClick={() => setFiltre('bientot')} aria-pressed={filtre === 'bientot'}>
          <span className="ad-stat-nombre">{stats.bientot}</span> expire{stats.bientot > 1 ? 'nt' : ''} sous 30 jours
        </button>
        <button type="button" className={`ad-stat ${filtre === 'expire' ? 'ad-stat-actif' : ''}`}
          onClick={() => setFiltre('expire')} aria-pressed={filtre === 'expire'}>
          <span className="ad-stat-nombre">{stats.expire}</span> expiré{stats.expire > 1 ? 's' : ''}
        </button>
        <button type="button" className={`ad-stat ${filtre === 'suspendu' ? 'ad-stat-actif' : ''}`}
          onClick={() => setFiltre('suspendu')} aria-pressed={filtre === 'suspendu'}>
          <span className="ad-stat-nombre">{stats.suspendu}</span> suspendu{stats.suspendu > 1 ? 's' : ''}
        </button>
        {(stats.attente > 0 || filtre === 'attente') && (
          <button type="button" className={`ad-stat ad-stat-attente ${filtre === 'attente' ? 'ad-stat-actif' : ''}`}
            onClick={() => setFiltre('attente')} aria-pressed={filtre === 'attente'}>
            <span className="ad-stat-nombre">{stats.attente}</span> poste{stats.attente > 1 ? 's' : ''} en attente
          </button>
        )}
      </div>

      <details className="ad-aide">
        <summary>Comment ça marche</summary>
        <ul>
          <li>
            Le service de fichiers du serveur du client envoie un signal toutes les 2 minutes ; lic.winalco.dz retient
            l&apos;adresse d&apos;où vient ce signal. Rien n&apos;est déclaré par le client.
          </li>
          <li>
            Au démarrage, un poste distant (SCF, GSCOM ou Paie) demande cette adresse. Il ne la reçoit que s&apos;il
            appartient au même client — par un poste autorisé dans « Postes », un jeton en usage direct ou sa
            licence — et que l&apos;abonnement n&apos;est ni expiré ni suspendu.
          </li>
          <li>
            « Postes » : les jetons qui font entrer les postes, et chaque poste, à approuver ou à révoquer à tout
            moment.
          </li>
          <li>
            Si l&apos;adresse affichée ici diffère de l&apos;adresse WAN du routeur du client, il est derrière un CGNAT
            (4G…) : l&apos;accès distant ne peut pas fonctionner. Voir la fiche technicien.
          </li>
        </ul>
      </details>

      <div className="filters">
        <input
          type="search"
          className="search-input"
          placeholder="Rechercher un client, un nom de serveur, une adresse…"
          aria-label="Rechercher un abonnement"
          value={recherche}
          onChange={(event) => setRecherche(event.target.value)}
        />
      </div>

      <div className="table-container">
        <table className="ad-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Serveur</th>
              <th>Adresse</th>
              <th>Dernier signal</th>
              <th>Abonnement</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="empty">Chargement...</td>
              </tr>
            )}
            {!loading && filteredItems.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {items.length === 0
                    ? 'Aucun abonnement. « Nouvel abonnement » pour commencer.'
                    : 'Aucun abonnement pour cette recherche.'}
                </td>
              </tr>
            )}
            {!loading && filteredItems.map((item) => {
              const etatConnexion = connexion(item);
              const pastille = pastilleExpiration(item);
              return (
                <tr key={item.id} className={item.actif ? '' : 'ad-ligne-suspendue'}>
                  <td>
                    <div className="ad-client">{item.client_name || '-'}</div>
                    {item.notes && <div className="ad-notes" title={item.notes}>{item.notes}</div>}
                  </td>
                  <td>
                    <div className="ad-mono">{item.nom_serveur}</div>
                    {item.service_version && (
                      <div className="ad-secondaire" title="Version du service de fichiers">
                        service {item.service_version}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="ad-mono">{item.ip || '-'}</div>
                    {item.ip_changee_le && (
                      <div className="ad-secondaire">depuis le {formatHorodatage(item.ip_changee_le)}</div>
                    )}
                  </td>
                  <td>
                    <span className={`ad-pastille ad-connexion-${etatConnexion}`}>
                      <span className={`ad-point ad-point-${etatConnexion}`} aria-hidden="true" />
                      {LIBELLES_CONNEXION[etatConnexion]}
                    </span>
                    <div className="ad-secondaire" title={formatHorodatage(item.vu_le)}>
                      {item.vu_le ? `vu ${formatVuIlYa(item.vu_le, maintenant)}` : 'aucun signal reçu'}
                    </div>
                  </td>
                  <td>
                    <div>{formatDateCourte(item.date_expiration)}</div>
                    <span className={`ad-pastille ad-expiration-${pastille.code}`}>{pastille.libelle}</span>
                  </td>
                  <td>
                    <div className="ad-actions">
                      {canRenew && (
                        <button className="btn btn-success" disabled={busyId === item.id} onClick={() => handleRenew(item)}
                          aria-label={`Renouveler ${item.nom_serveur}`}>
                          Renouveler
                        </button>
                      )}
                      {canEdit && (
                        <button className="btn btn-secondary" disabled={busyId === item.id} onClick={() => openEdit(item)}
                          aria-label={`Modifier ${item.nom_serveur}`}>
                          Modifier
                        </button>
                      )}
                      {canEdit && item.actif && (
                        <button className="btn btn-warning" disabled={busyId === item.id} onClick={() => handleSuspend(item)}
                          aria-label={`Suspendre ${item.nom_serveur}`}>
                          Suspendre
                        </button>
                      )}
                      {canEdit && !item.actif && (
                        <button className="btn btn-success" disabled={busyId === item.id} onClick={() => handleResume(item)}
                          aria-label={`Réactiver ${item.nom_serveur}`}>
                          Réactiver
                        </button>
                      )}
                      {canEdit && (
                        <button className="btn btn-secondary" disabled={busyId === item.id} onClick={() => handleRegenerate(item)}
                          aria-label={`Nouveau jeton pour ${item.nom_serveur}`}>
                          Nouveau jeton
                        </button>
                      )}
                      <button className="btn btn-secondary" disabled={busyId === item.id} onClick={() => setPostesDe(item)}
                        aria-label={`Postes de ${item.nom_serveur}` +
                          (item.postes_en_attente > 0 ? `, ${item.postes_en_attente} en attente` : '')}>
                        Postes
                        {item.postes_en_attente > 0 && (
                          <span className="ad-badge" aria-hidden="true">{item.postes_en_attente}</span>
                        )}
                      </button>
                      {canDelete && (
                        <button className="btn btn-secondary ad-btn-danger" disabled={busyId === item.id}
                          onClick={() => handleDelete(item)} aria-label={`Supprimer ${item.nom_serveur}`}>
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ad-modal-titre">
            <h2 id="ad-modal-titre">{modal.mode === 'create' ? 'Nouvel abonnement' : 'Modifier l’abonnement'}</h2>
            {modal.mode === 'create' && (
              <p className="ad-info">
                Le jeton du serveur est généré à l&apos;enregistrement et <strong>affiché une seule fois</strong>.
              </p>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="ad-client-recherche">Client</label>
                <input
                  id="ad-client-recherche"
                  type="search"
                  placeholder="Filtrer la liste des clients…"
                  value={rechercheClient}
                  onChange={(event) => setRechercheClient(event.target.value)}
                />
                <select
                  id="ad-client"
                  aria-label="Client"
                  className="ad-select-client"
                  value={modal.form.id_client}
                  onChange={(event) => choisirClient(event.target.value)}
                  size={Math.min(6, Math.max(2, optionsClients.length + 1))}
                >
                  <option value="">— Choisir le client —</option>
                  {optionsClients.map((client) => (
                    <option key={client.id} value={String(client.id)}>{client.client_name || `Client ${client.id}`}</option>
                  ))}
                </select>
                {formCheck.client && modal.mode === 'create' && <p className="ad-field-hint">{formCheck.client}</p>}
                {modal.mode === 'edit' && modal.initial.id_client !== modal.form.id_client && (
                  <p className="ad-field-error">Changer de client : seuls les postes du nouveau client recevront l&apos;adresse.</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="ad-nom">Nom du serveur</label>
                <input
                  id="ad-nom"
                  className="ad-mono"
                  placeholder="ex. sarl-exemple"
                  value={modal.form.nom_serveur}
                  onChange={(event) =>
                    setModal((courant) => courant
                      ? { ...courant, nomModifie: true, form: { ...courant.form, nom_serveur: event.target.value.toLowerCase() } }
                      : courant)}
                  aria-invalid={Boolean(formCheck.nom) && modal.form.nom_serveur !== ''}
                  maxLength={40}
                />
                {formCheck.nom && modal.form.nom_serveur !== '' ? (
                  <p className="ad-field-error">{formCheck.nom}</p>
                ) : (
                  <p className="ad-field-hint">
                    Server_Name du serveur du client et des postes. Proposé à partir du nom du client, modifiable.
                  </p>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ad-debut">Début</label>
                  <input id="ad-debut" type="date" value={modal.form.date_debut}
                    onChange={(event) => changerDebut(event.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="ad-expiration">Expiration</label>
                  <input id="ad-expiration" type="date" value={modal.form.date_expiration}
                    disabled={modal.mode === 'edit' && !canRenew}
                    onChange={(event) => updateForm({ date_expiration: event.target.value })} />
                  {modal.mode === 'edit' && !canRenew && (
                    <p className="ad-field-hint">Changer l&apos;expiration demande le droit de renouveler.</p>
                  )}
                </div>
              </div>
              {formCheck.dates && <p className="ad-field-error">{formCheck.dates}</p>}
              {modal.mode === 'create' && (
                <p className="ad-field-hint">Le jour de l&apos;expiration est encore couvert (date d&apos;Alger).</p>
              )}

              <div className="form-group">
                <label htmlFor="ad-notes">Notes</label>
                <textarea
                  id="ad-notes"
                  rows={3}
                  placeholder="ex. Payé le 17/09/2026, facture 2026-123"
                  value={modal.form.notes}
                  onChange={(event) => updateForm({ notes: event.target.value })}
                />
                <p className={formCheck.notes ? 'ad-field-error' : 'ad-field-hint'}>
                  {modal.form.notes.length.toLocaleString('fr-FR')} / {NOTES_MAX.toLocaleString('fr-FR')} caractères
                </p>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={!formCheck.valid || saving}>
                  {saving ? 'Enregistrement…' : modal.mode === 'create' ? 'Créer l’abonnement' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {postesDe && (
        <DdnsPostes
          serveur={postesDe}
          canEdit={canEdit}
          canDelete={canDelete}
          onFermer={() => setPostesDe(null)}
          onModifie={() => loadData(true)}
          onJeton={(valeur, directJusquAu) => montrerJetonPostes(postesDe, valeur, directJusquAu)}
        />
      )}

      {jeton && (
        <div className="modal-overlay" role="presentation">
          <div className="modal ad-jeton" role="dialog" aria-modal="true" aria-labelledby="ad-jeton-titre">
            <h2 id="ad-jeton-titre">
              {jeton.contexte === 'creation'
                ? 'Abonnement créé'
                : jeton.contexte === 'postes'
                  ? 'Jeton de postes'
                  : 'Nouveau jeton'} : <span className="ad-mono">{jeton.nom}</span>
            </h2>
            {jeton.contexte === 'postes' ? (
              <p className="ad-alerte" role="alert">
                <strong>Le jeton ne sera plus jamais affiché.</strong> Copiez ce bloc maintenant sur
                {' '}<strong>les postes distants</strong> du client{jeton.client ? ` ${jeton.client}` : ''} à faire
                entrer. Chaque poste s’en sert une fois pour se présenter, puis apparaît dans « Postes » pour
                approbation.
                {jeton.directJusquAu
                  ? ` Les logiciels pas encore mis à jour l’utilisent directement, jusqu’au ${formatDateCourte(jeton.directJusquAu)}.`
                  : ' Usage direct refusé : seuls les logiciels à jour peuvent s’en servir.'}
              </p>
            ) : (
              <p className="ad-alerte" role="alert">
                <strong>Le jeton ne sera plus jamais affiché.</strong> Copiez ce bloc maintenant dans
                {' '}<code>WinalcoFichiers.ini</code>, sur le serveur du client{jeton.client ? ` ${jeton.client}` : ''},
                puis redémarrez le service WinalcoFichiersService.
                {jeton.contexte === 'regeneration' && ' L’ancien jeton ne fonctionne plus.'}
              </p>
            )}
            {/* Le jeton du SERVICE ne se montre jamais dans le contexte 'postes' : il autorise à
                ÉCRIRE l'adresse du serveur, et n'a rien à faire sur un poste. */}
            {jeton.contexte !== 'postes' && (
              <BlocIni
                id="ad-bloc-serveur"
                titre="Serveur du client : WinalcoFichiers.ini"
                texte={blocIniServeur(jeton.nom, jeton.jeton)}
                onCopier={(texte) => copier(texte, true)}
              />
            )}
            <BlocIni
              id="ad-bloc-poste"
              titre="Postes distants : Winalco_Scf.ini, Gscom.ini ou PAIE.ini"
              texte={blocIniPoste(jeton.nom, jeton.contexte === 'postes' ? jeton.jeton : undefined)}
              onCopier={(texte) => copier(texte, jeton.contexte === 'postes')}
            />
            <p className="ad-field-hint">
              Sur les postes <strong>hors du bureau</strong> seulement : au bureau, le poste garde son adresse locale
              (laisser la section absente ou Actif=0).
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={fermerJeton}>
                {jeton.copie ? 'Terminé' : 'Fermer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ddns;
